import {
  AuthContext
} from "./chunk-52JZDGH7.js";
import {
  defaultPolicyRegistry,
  getCapabilities,
  getEffectiveRoles,
  hasCapability,
  hasRole
} from "./chunk-U523KRWQ.js";

// src/policy/react.tsx
import {
  createContext,
  useContext as useContext2,
  useEffect,
  useMemo,
  useState
} from "react";

// src/react/useAuth.ts
import { useContext } from "react";
function useAuth() {
  return useContext(AuthContext);
}

// src/policy/react.tsx
import { Fragment, jsx } from "react/jsx-runtime";
var PolicyRegistryContext = createContext(defaultPolicyRegistry);
var GatewayAuthorityContext = createContext(null);
function GatewayAuthorityProvider({
  authority,
  children
}) {
  return /* @__PURE__ */ jsx(GatewayAuthorityContext.Provider, { value: authority, children });
}
function useGatewayAuthority() {
  return useContext2(GatewayAuthorityContext);
}
function useGatewayPermission(roles, strategy = "ANY_OF") {
  const authority = useGatewayAuthority();
  const { user, isAuthenticated, isLoading } = useAuth();
  const roleKey = roles.join("");
  const [state, setState] = useState({
    granted: false,
    loading: Boolean(authority),
    authoritative: false,
    error: false
  });
  useEffect(() => {
    if (!authority) {
      setState({ granted: false, loading: false, authoritative: false, error: false });
      return void 0;
    }
    if (isLoading) {
      setState({ granted: false, loading: true, authoritative: false, error: false });
      return void 0;
    }
    if (!isAuthenticated || !user) {
      setState({ granted: false, loading: false, authoritative: false, error: false });
      return void 0;
    }
    let active = true;
    setState({ granted: false, loading: true, authoritative: false, error: false });
    authority.evaluate(roles, strategy).then((result) => {
      if (!active) return;
      setState({
        granted: result.authoritative && result.granted,
        loading: false,
        authoritative: result.authoritative,
        error: !result.authoritative
      });
    }).catch(() => {
      if (!active) return;
      setState({ granted: false, loading: false, authoritative: false, error: true });
    });
    return () => {
      active = false;
    };
  }, [authority, isAuthenticated, isLoading, roleKey, strategy, user?.id, user?.tenantId, user?.accessToken, user?.expiresAt]);
  return state;
}
function PolicyRegistryProvider({
  registry,
  children
}) {
  return /* @__PURE__ */ jsx(PolicyRegistryContext.Provider, { value: registry, children });
}
function useRegistry() {
  return useContext2(PolicyRegistryContext);
}
function useCapabilityState(appId, capability) {
  const registry = useRegistry();
  const { user } = useAuth();
  const authority = useGatewayAuthority();
  const policy = registry.get(appId);
  const requiredRoles = policy?.capabilities[capability] ?? [];
  const gateway = useGatewayPermission(requiredRoles);
  const localGranted = useMemo(
    () => hasCapability(appId, capability, user, registry),
    [appId, capability, user, registry]
  );
  return authority ? gateway : { granted: localGranted, loading: false, authoritative: false, error: false };
}
function useCapability(appId, capability) {
  const state = useCapabilityState(appId, capability);
  return state.granted && (!useGatewayAuthority() || state.authoritative);
}
function useHasRole(appId, role) {
  const registry = useRegistry();
  const { user } = useAuth();
  const authority = useGatewayAuthority();
  const gateway = useGatewayPermission([role], "ANY_OF");
  const localGranted = useMemo(
    () => hasRole(appId, role, user, registry),
    [appId, role, user, registry]
  );
  return authority ? gateway.granted && gateway.authoritative && Boolean(registry.get(appId)?.acceptedRoles.includes(role)) : localGranted;
}
function useCapabilities(appId) {
  const registry = useRegistry();
  const { user } = useAuth();
  const authority = useGatewayAuthority();
  const policy = registry.get(appId);
  const entries = Object.entries(policy?.capabilities ?? {});
  const [gatewayCapabilities, setGatewayCapabilities] = useState([]);
  const { isAuthenticated, isLoading } = useAuth();
  const entryKey = entries.map(([cap, roles]) => `${cap}:${roles.join(",")}`).join("|");
  const userIdentity = user ? `${user.id}|${user.tenantId}|${user.accessToken}|${user.expiresAt}` : "";
  useEffect(() => {
    if (!authority) {
      setGatewayCapabilities([]);
      return void 0;
    }
    if (isLoading || !isAuthenticated || !user || entries.length === 0) {
      setGatewayCapabilities([]);
      return void 0;
    }
    let active = true;
    setGatewayCapabilities([]);
    Promise.all(
      entries.map(async ([capability, roles]) => {
        const result = await authority.evaluate(roles, "ANY_OF");
        return result.authoritative && result.granted ? capability : null;
      })
    ).then((results) => {
      if (active) setGatewayCapabilities(results.filter((value) => value !== null));
    }).catch(() => {
      if (active) setGatewayCapabilities([]);
    });
    return () => {
      active = false;
    };
  }, [authority, entryKey, isAuthenticated, isLoading, userIdentity]);
  const localCapabilities = useMemo(
    () => getCapabilities(appId, user, registry),
    [appId, user, registry]
  );
  return authority ? gatewayCapabilities : localCapabilities;
}
function useEffectiveRoles(appId) {
  const registry = useRegistry();
  const { user } = useAuth();
  const authority = useGatewayAuthority();
  const policy = registry.get(appId);
  const acceptedRoles = policy?.acceptedRoles ?? [];
  const acceptedRoleKey = acceptedRoles.join("|");
  const [gatewayRoles, setGatewayRoles] = useState([]);
  const { isAuthenticated, isLoading } = useAuth();
  const userIdentity = user ? `${user.id}|${user.tenantId}|${user.accessToken}|${user.expiresAt}` : "";
  useEffect(() => {
    if (!authority) {
      setGatewayRoles([]);
      return void 0;
    }
    if (isLoading || !isAuthenticated || !user || acceptedRoles.length === 0) {
      setGatewayRoles([]);
      return void 0;
    }
    let active = true;
    Promise.all(acceptedRoles.map(async (role) => {
      const result = await authority.evaluate([role], "ANY_OF");
      return result.authoritative && result.granted ? role : null;
    })).then((roles) => {
      if (active) setGatewayRoles(roles.filter((role) => role !== null));
    }).catch(() => {
      if (active) setGatewayRoles([]);
    });
    return () => {
      active = false;
    };
  }, [acceptedRoleKey, authority, isAuthenticated, isLoading, userIdentity]);
  const localRoles = useMemo(
    () => getEffectiveRoles(appId, user, registry),
    [appId, user, registry]
  );
  return authority ? gatewayRoles : localRoles;
}
function useAppAccess(appId) {
  return useCapability(appId, "app.access");
}
function RequireCapability({
  appId,
  capability,
  children,
  fallback = null,
  loadingFallback = null
}) {
  const authority = useGatewayAuthority();
  const state = useCapabilityState(appId, capability);
  const granted = state.granted && (!authority || state.authoritative);
  if (authority && state.loading) return /* @__PURE__ */ jsx(Fragment, { children: loadingFallback });
  return /* @__PURE__ */ jsx(Fragment, { children: granted ? children : fallback });
}
function AppAccessGate({
  appId,
  children,
  loadingFallback = null,
  deniedFallback = null,
  unauthenticatedFallback = null
}) {
  const { isLoading, isAuthenticated } = useAuth();
  const authority = useGatewayAuthority();
  const state = useCapabilityState(appId, "app.access");
  const granted = state.granted && (!authority || state.authoritative);
  if (isLoading) return /* @__PURE__ */ jsx(Fragment, { children: loadingFallback });
  if (!isAuthenticated) return /* @__PURE__ */ jsx(Fragment, { children: unauthenticatedFallback });
  if (authority && state.loading) return /* @__PURE__ */ jsx(Fragment, { children: loadingFallback });
  if (!granted) return /* @__PURE__ */ jsx(Fragment, { children: deniedFallback });
  return /* @__PURE__ */ jsx(Fragment, { children });
}

export {
  useAuth,
  GatewayAuthorityProvider,
  useGatewayAuthority,
  useGatewayPermission,
  PolicyRegistryProvider,
  useCapability,
  useHasRole,
  useCapabilities,
  useEffectiveRoles,
  useAppAccess,
  RequireCapability,
  AppAccessGate
};
//# sourceMappingURL=chunk-RJWLBNM2.js.map